import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Divider,
  Dropdown,
  Empty,
  Grid,
  Input,
  message,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  Tooltip,
} from "antd";
import {
  ExclamationCircleFilled,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  UserAddOutlined,
  DeleteOutlined,
  SearchOutlined,
  KeyOutlined,
  UserSwitchOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { LockOutlined } from "@ant-design/icons";

import { useAppDispatch } from "../../../hooks/reduxHooks";
import { useAppSelector } from "../../../hooks/reduxHooks";
import {
  changeUserRoleApi,
  getAllUsersApi,
  userDeletionApi,
  userPasswordResetApi,
} from "../../../services/users.service";
import InviteUserModal from "../../../components/inviteUserModal";
import UpgradeDialog from "../../../components/common/UpgradeDialog";
import { formatDateTime } from "../../../utils/formatDateTime";

const ManageAgentsComponent = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { isPaidPlan } = useAppSelector((state) => state.currentPlan);

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [searchKeywords, setSearchKeywords] = useState("");
  const [userRole, setUserRole] = useState();
  const [actionLoading, setActionLoading] = useState({});

  const { confirm } = Modal;
  const { Text, Title } = Typography;

  // Pagination
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalDocuments, setTotalDocuments] = useState(0);

  const [usersList, setUsersList] = useState([]);

  const [isUserInviteModalVisible, setIsUserInviteModalVisible] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const currentUserId = localStorage.getItem("userId");
  const currentUserRole = localStorage.getItem("role");

  const roleOptions = [
    { label: "All Roles", value: null },
    { label: "Admin", value: "admin" },
    { label: "Agent", value: "agent" },
  ];

  const handlePasswordReset = (subordinate_id, username) => {
    setActionLoading((prev) => ({ ...prev, [subordinate_id]: "reset" }));
    dispatch(
      userPasswordResetApi({
        reqData: {
          subordinate_id: subordinate_id,
        },
        finalCallback: () => {
          setActionLoading((prev) => ({ ...prev, [subordinate_id]: null }));
        },
        successCallback: (response) => {
          if (response) {
            const regex = /reset to ([a-zA-Z0-9]+) successfully/;
            const match = response.message.match(regex);
            const resetPassword = match ? match[1] : "unknown";

            Modal.success({
              title: (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 20 }} />
                  <span>Password Reset Successful</span>
                </div>
              ),
              icon: null,
              width: 480,
              content: (
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">
                    {username}'s password has been reset to:
                  </Text>
                  <div
                    style={{
                      marginTop: 12,
                      padding: "12px 16px",
                      background: "#f6ffed",
                      border: "1px solid #b7eb8f",
                      borderRadius: 8,
                      textAlign: "center",
                    }}
                  >
                    <Text
                      copyable={{ text: resetPassword }}
                      style={{ fontSize: 18, fontWeight: 600, fontFamily: "monospace" }}
                    >
                      {resetPassword}
                    </Text>
                  </div>
                  <Text
                    type="secondary"
                    style={{ display: "block", marginTop: 12, fontSize: 13 }}
                  >
                    Please share this password with <strong>{username}</strong> and
                    remind them to change it after logging in.
                  </Text>
                </div>
              ),
            });
          }
        },
        failureCallback: () => {
          Modal.error({
            title: "Password Reset Failed",
            content: `Failed to reset the password for ${username}. Please try again later.`,
          });
        },
      })
    );
  };

  const handleRoleChange = (subordinate_id, new_role) => {
    setActionLoading((prev) => ({ ...prev, [subordinate_id]: "role" }));
    dispatch(
      changeUserRoleApi({
        params: {
          user_id: subordinate_id,
          role_update_request: new_role,
        },
        finalCallback: () => {
          setActionLoading((prev) => ({ ...prev, [subordinate_id]: null }));
        },
        successCallback: (response) => {
          if (response) {
            message.success(response.message || "Role updated successfully!");
            fetchUsers();
          }
        },
        failureCallback: (error) => {
          Modal.error({
            title: "Role Change Failed",
            icon: <CloseCircleTwoTone twoToneColor="#ff4d4f" />,
            content: (
              <div>
                <p>{error.message}</p>
                {error.detail && <Text type="secondary">{error.detail}</Text>}
              </div>
            ),
          });
        },
      })
    );
  };

  const handleUserDelete = (user) => {
    confirm({
      title: "Delete User",
      icon: <ExclamationCircleFilled style={{ color: "#ff4d4f" }} />,
      content: (
        <div>
          <Text>
            Are you sure you want to delete <strong>{user.username}</strong>?
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 13 }}>
            This action cannot be undone.
          </Text>
        </div>
      ),
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        setActionLoading((prev) => ({ ...prev, [user._id]: "delete" }));
        dispatch(
          userDeletionApi({
            params: { user_id: user._id },
            finalCallback: () => {
              setActionLoading((prev) => ({ ...prev, [user._id]: null }));
            },
            successCallback: () => {
              message.success("User deleted successfully!");
              fetchUsers();
            },
            failureCallback: () => {},
          })
        );
      },
    });
  };

  const showRoleChangeConfirm = (user, newRole) => {
    confirm({
      title: "Change Role",
      icon: <UserSwitchOutlined style={{ color: "#1890ff" }} />,
      content: (
        <div>
          <Text>
            Change <strong>{user.username}</strong>'s role to <strong>{newRole.charAt(0).toUpperCase() + newRole.slice(1)}</strong>?
          </Text>
        </div>
      ),
      okText: "Change Role",
      cancelText: "Cancel",
      onOk() {
        handleRoleChange(user._id, newRole);
      },
    });
  };

  const showPasswordResetConfirm = (user) => {
    confirm({
      title: "Reset Password",
      icon: <KeyOutlined style={{ color: "#faad14" }} />,
      content: (
        <div>
          <Text>
            Are you sure you want to reset <strong>{user.username}</strong>'s password?
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 13 }}>
            This will generate a new temporary password for the user.
          </Text>
        </div>
      ),
      okText: "Reset Password",
      cancelText: "Cancel",
      onOk() {
        handlePasswordReset(user._id, user.username);
      },
    });
  };

  const fetchUsers = () => {
    setLoading(true);
    dispatch(
      getAllUsersApi({
        params: {
          page_number: pageNumber,
          documents_per_page: pageSize,
          agent_search_query: searchKeywords || undefined,
          role_filter: userRole || undefined,
        },
        finalCallback: () => {
          setLoading(false);
        },
        successCallback: (response) => {
          if (response) {
            setTotalDocuments(response?.total_documents || 0);
            setUsersList(response?.documents || []);
          }
        },
        failureCallback: () => {},
      })
    );
  };

  useEffect(() => {
    fetchUsers();
  }, [pageNumber, pageSize, userRole, searchKeywords]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageNumber(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchKeywords]);

  const columns = [
    {
      title: "User",
      dataIndex: "username",
      key: "username",
      render: (username, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            size={isMobile ? 32 : 36}
            style={{
              backgroundColor: "#1890ff",
              fontWeight: 500,
            }}
          >
            {username?.charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500 }}>
              {username}
              {record._id === currentUserId && (
                <Tag
                  style={{
                    marginLeft: 8,
                    fontSize: 11,
                  }}
                  color="green"
                >
                  You
                </Tag>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 120,
      render: (role) => (
        <Tag color={role === "admin" ? "blue" : "cyan"}>
          {role?.charAt(0).toUpperCase() + role?.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (date) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {formatDateTime({ date, withTime: false })}
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: isMobile ? 220 : 280,
      render: (_, record) => {
        const isCurrentUser = record._id === currentUserId;
        const isAdmin = currentUserRole === "admin";
        const newRole = record.role === "admin" ? "agent" : "admin";

        return (
          <Space size="small" direction={isMobile ? "vertical" : "horizontal"}>
            <Tooltip title="Reset Password">
              <Button
                size="small"
                icon={<KeyOutlined />}
                variant="filled"
                color="primary"
                loading={actionLoading[record._id] === "reset"}
                onClick={() => showPasswordResetConfirm(record)}
              >
                Reset Password
              </Button>
            </Tooltip>

            {isAdmin && !isCurrentUser ? (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: newRole,
                      label: newRole.charAt(0).toUpperCase() + newRole.slice(1),
                      onClick: () => showRoleChangeConfirm(record, newRole),
                    },
                  ],
                }}
                trigger={["click"]}
              >
                <Button size="small" icon={<UserSwitchOutlined />} variant="filled" color="default" loading={actionLoading[record._id] === "role"}>
                  Change Role
                </Button>
              </Dropdown>
            ) : (
              !isCurrentUser && null
            )}

            {isAdmin && !isCurrentUser && (
              <Tooltip title="Delete User">
                <Button
                  size="small"
                  danger
                  variant="filled"
                  icon={<DeleteOutlined />}
                  loading={actionLoading[record._id] === "delete"}
                  onClick={() => handleUserDelete(record)}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Card>
      <Title level={4} style={{ textAlign: "left", marginBottom: "8px" }}>
        Manage Users
      </Title>
      <Text type="secondary">
        Manage your team members and their account permissions
      </Text>

      <Divider style={{ margin: "16px 0" }} />

      {/* Search and Filter Bar */}
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "nowrap",
            width: isMobile ? "100%" : "auto",
            alignItems: "center",
          }}
        >
          <Input
            placeholder="Search by username..."
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            allowClear
            value={searchKeywords}
            onChange={(e) => setSearchKeywords(e.target.value)}
            style={{ width: isMobile ? "100%" : 280 }}
          />
          <Dropdown
            menu={{
              items: roleOptions.map((opt) => ({
                key: opt.value || "all",
                label: opt.label,
                onClick: () => {
                  setUserRole(opt.value);
                  setPageNumber(1);
                },
              })),
              selectedKeys: [userRole || "all"],
            }}
            trigger={["hover"]}
          >
            <Button icon={<FilterOutlined />} style={{ whiteSpace: "nowrap" }}>
              {roleOptions.find((o) => o.value === userRole)?.label || "All Roles"}
            </Button>
          </Dropdown>
        </div>
        <div style={{ flex: 1, minWidth: isMobile ? "100%" : "auto" }} />
        <Button
          type="primary"
          icon={isPaidPlan ? <UserAddOutlined /> : <LockOutlined style={{ fontSize: 12 }} />}
          onClick={() => {
            if (!isPaidPlan) {
              setShowUpgradeDialog(true);
            } else {
              setIsUserInviteModalVisible(true);
            }
          }}
          style={{ width: isMobile ? "100%" : "auto" }}
        >
          Invite User
        </Button>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={usersList}
        rowKey="_id"
        loading={loading}
        tableLayout="auto"
        pagination={{
          current: pageNumber,
          pageSize: pageSize,
          total: totalDocuments,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} members`,
          pageSizeOptions: ["5", "10", "20", "50"],
          onChange: (page, size) => {
            setPageNumber(page);
            setPageSize(size);
          },
        }}
        scroll={{ x: "max-content" }}
        size={isMobile ? "small" : "middle"}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <Text type="secondary">No team members found</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {searchKeywords || userRole
                      ? "Try adjusting your search or filter"
                      : "Invite team members to get started"}
                  </Text>
                </div>
              }
            >
              {!searchKeywords && !userRole && isPaidPlan && (
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={() => setIsUserInviteModalVisible(true)}
                >
                  Invite User
                </Button>
              )}
            </Empty>
          ),
        }}
      />

      {/* Modals */}
      <InviteUserModal
        isUserInviteModalVisible={isUserInviteModalVisible}
        setIsUserInviteModalVisible={setIsUserInviteModalVisible}
      />
      <UpgradeDialog
        visible={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        feature="Inviting users"
      />
    </Card>
  );
};

export default ManageAgentsComponent;
